import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { DocPoint } from './features'
import { archiveErrorMessage } from './messages'
import { imageDimensions, lightboxResize, rasterizeBitmap } from './playback'
import { readPhotoBlob } from './readPhoto'

type LightboxImage = { image: HTMLCanvasElement | null; error: string | null }

const EMPTY: LightboxImage = { image: null, error: null }

export const useLightboxImage = (open: boolean, file: Blob, point: DocPoint | null): LightboxImage => {
  const { t } = useTranslation('documentation')
  const [state, setState] = useState<LightboxImage>(EMPTY)

  useEffect(() => {
    setState(EMPTY)
    if (!open || !point) return
    const photo = point.image
    if (!photo) {
      setState({
        image: null,
        error: point.imagePath ? t('photoMissing', { path: point.imagePath }) : t('noImagePath'),
      })
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const blob = await readPhotoBlob(file, photo)
        const resize = lightboxResize(await imageDimensions(blob))
        const bitmap = await createImageBitmap(blob, resize ? { ...resize, resizeQuality: 'high' } : undefined)
        if (cancelled) bitmap.close()
        else setState({ image: rasterizeBitmap(bitmap), error: null })
      } catch (error) {
        if (!cancelled) setState({ image: null, error: archiveErrorMessage(t, error) })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [file, open, point, t])

  return state
}
